import axios from 'axios'
import { API_BASE_URL, STORAGE_KEYS } from '../utils/constants'

// Session error codes
const SESSION_ERROR_CODES = ['USER_BLOCKED', 'SESSION_LOCKED', 'SESSION_INVALID']

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Accept': 'application/json',
    'Content-Type': 'application/json',
  },
  timeout: 30000,
})

// Request interceptor to add token (only if available)
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN)
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    
    // If FormData, remove Content-Type to let browser set it with boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type']
    }
    
    // Allow requests without token for public endpoints
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor to handle errors
apiClient.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    if (error.response?.status === 401) {
      // Only redirect if user was authenticated (had a token)
      const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN)
      if (token) {
        // User had token but it's invalid - clear and redirect
        localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN)
        localStorage.removeItem(STORAGE_KEYS.USER)
        // Only redirect if not already on login/register page, course detail page, instructor profile page, package pages, or subscriptions page
        // These pages handle 401 errors gracefully with login banner
        const isCourseDetailPage = /^\/courses\/\d+$/.test(window.location.pathname)
        const isInstructorProfilePage = /^\/instructors\/\d+$/.test(window.location.pathname)
        const isPackagePage = /^\/packages\/\d+\/subjects$/.test(window.location.pathname)
        const isSubscriptionsPage = window.location.pathname === '/subscriptions'
        if (!window.location.pathname.includes('/login') && 
            !window.location.pathname.includes('/register') &&
            !window.location.pathname.includes('/forgot-password') &&
            !isCourseDetailPage &&
            !isInstructorProfilePage &&
            !isPackagePage &&
            !isSubscriptionsPage) {
          window.location.href = '/login'
        }
      }
      // If no token, it's a public endpoint that requires auth
      // Don't redirect - let the component handle it gracefully
    }
    
    // Handle session-related 403 errors
    if (error.response?.status === 403 && error.response?.data?.error_code) {
      const errorCode = error.response.data.error_code
      
      // If session is blocked, locked, or invalid, clear storage and redirect
      if (SESSION_ERROR_CODES.includes(errorCode)) {
        localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN)
        localStorage.removeItem(STORAGE_KEYS.USER)
        
        // Store the error message to show on login page
        const message = error.response.data.message || 'Your session has been terminated'
        sessionStorage.setItem('auth_error', JSON.stringify({ errorCode, message }))
        
        // Redirect to login if not already there
        if (!window.location.pathname.includes('/login') && 
            !window.location.pathname.includes('/register')) {
          window.location.href = '/login'
        }
      }
    }
    
    return Promise.reject(error)
  }
)

export default apiClient
